import { FeatureNames } from 'owa-mail-smart-pill-features';
import { SmartPillData, SmartPillFeedbackData } from 'owa-smart-pill';
import { ClientItem } from 'owa-mail-store';
import { trace } from 'owa-trace';
import loc from 'owa-localize';
import {
    SmartPillFeedbackMeetingSuggestionSubtitle,
    SmartPillFeedbackFileSuggestionsSubtitle,
    SmartPillFeedbackGenericSubtitle,
    SmartPillFeedbackGenericOption1,
    SmartPillFeedbackGenericOption2,
    SmartPillFeedbackGenericOption3,
    SmartPillFeedbackGenericOption4,
    SmartPillFeedbackGenericOption5,
    SmartPillFeedbackGenericOption6,
    SmartPillFeedbackGenericOption7,
    SmartPillFeedbackMeetingOption1,
    SmartPillFeedbackMeetingOption2,
    SmartPillFeedbackMeetingOption3,
    SmartPillFeedbackMeetingOption4,
    SmartPillFeedbackMeetingOption5,
    SmartPillFeedbackFileOption1,
    SmartPillFeedbackFileOption2,
    SmartPillFeedbackFileOption3,
    SmartPillFeedbackFileOption4,
    SmartPillFeedbackFileOption5,
} from './getFeedbackData.locstring.json';

// This object consists of the unlocalized English logstrings (strings for feedback logging) of the
// corresponding feedback options listed in "./getFeedbackData.locstring.json".
// The strings in this object should be in sync with the strings in "./getFeedbackData.locstring.json".
const SMART_PILL_LOGSTRING = {
    SmartPillFeedbackGenericOption1: 'Not relevant',
    SmartPillFeedbackGenericOption2: 'Too similar',
    SmartPillFeedbackGenericOption3: 'No reply needed',
    SmartPillFeedbackGenericOption4: "Doesn't sound like me",
    SmartPillFeedbackGenericOption5: 'Offensive or rude',
    SmartPillFeedbackGenericOption6: 'Not in the correct language',
    SmartPillFeedbackGenericOption7: 'Other',
    SmartPillFeedbackMeetingOption1: 'The meeting is already on my calendar',
    SmartPillFeedbackMeetingOption2: "A meeting suggestion isn't relevant",
    SmartPillFeedbackMeetingOption3: "I'm not the person who should schedule the meeting",
    SmartPillFeedbackMeetingOption4: 'I want to schedule the meeting later',
    SmartPillFeedbackMeetingOption5: "I don't want help scheduling a meeting",
    SmartPillFeedbackFileOption1: '"Attach a file" suggestion isn\'t relevant',
    SmartPillFeedbackFileOption2: "I'm not the one who needs to attach a file",
    SmartPillFeedbackFileOption3: "Didn't find the file I was looking for",
    SmartPillFeedbackFileOption4: "Didn't get any file suggestions",
    SmartPillFeedbackFileOption5: 'Want to attach a file later',
};

// Getting the feedback data based on the pills we are showing can be done by the arbitrator, but since
// the feedback form is an optional component, we just have a separate function to take care of it.
// All of our arbitrators will likely use the same logic for generating feedback data anyway.
export default function getFeedbackData(
    pills: SmartPillData[],
    item: ClientItem
): SmartPillFeedbackData {
    let messageBody: string;

    if (item.UniqueBody) {
        messageBody = item.UniqueBody.Value;
    } else if (item.NormalizedBody) {
        messageBody = item.NormalizedBody.Value;
    }

    // donationJson is always the same
    const feedbackData = {
        subtitle: undefined,
        dSATOptions: undefined,
        donationJson: {
            messageBody: messageBody,
        },
    };

    const hasSmartTimePill: boolean = pills.some(
        pill => pill.featureName === FeatureNames.SmartTime
    );
    const hasSmartDocPill: boolean = pills.some(pill => pill.featureName === FeatureNames.SmartDoc);
    const hasSmartReplyPills: boolean = pills.some(
        pill => pill.featureName === FeatureNames.SmartReply
    );

    if (hasSmartTimePill && hasSmartDocPill) {
        trace.warn(
            '[SmartPillFeedbackData] feedback options not defined for Smart Time + Smart Doc'
        );
    }

    if (hasSmartReplyPills) {
        // Smart replies can be mixed with other rich entities
        feedbackData.subtitle = loc(SmartPillFeedbackGenericSubtitle);

        if (hasSmartTimePill) {
            // Case 1: Smart time and smart reply pills
            feedbackData.dSATOptions = [
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption1,
                    uiString: loc(SmartPillFeedbackGenericOption1),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption4,
                    uiString: loc(SmartPillFeedbackGenericOption4),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption5,
                    uiString: loc(SmartPillFeedbackGenericOption5),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption3,
                    uiString: loc(SmartPillFeedbackMeetingOption3),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption5,
                    uiString: loc(SmartPillFeedbackMeetingOption5),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption7,
                    uiString: loc(SmartPillFeedbackGenericOption7),
                },
            ];
        } else if (hasSmartDocPill) {
            // Case 2: Smart Doc and smart reply pills
            feedbackData.dSATOptions = [
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption1,
                    uiString: loc(SmartPillFeedbackGenericOption1),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption5,
                    uiString: loc(SmartPillFeedbackGenericOption5),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption2,
                    uiString: loc(SmartPillFeedbackFileOption2),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption3,
                    uiString: loc(SmartPillFeedbackFileOption3),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption5,
                    uiString: loc(SmartPillFeedbackFileOption5),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption7,
                    uiString: loc(SmartPillFeedbackGenericOption7),
                },
            ];
        } else {
            // Case 3: Smart reply by itself
            feedbackData.dSATOptions = [
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption1,
                    uiString: loc(SmartPillFeedbackGenericOption1),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption2,
                    uiString: loc(SmartPillFeedbackGenericOption2),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption3,
                    uiString: loc(SmartPillFeedbackGenericOption3),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption4,
                    uiString: loc(SmartPillFeedbackGenericOption4),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption5,
                    uiString: loc(SmartPillFeedbackGenericOption5),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption6,
                    uiString: loc(SmartPillFeedbackGenericOption6),
                },
                {
                    logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption7,
                    uiString: loc(SmartPillFeedbackGenericOption7),
                },
            ];
        }
    } else if (hasSmartTimePill) {
        // Case 4: Just a smart time pill by itself
        feedbackData.subtitle = loc(SmartPillFeedbackMeetingSuggestionSubtitle);
        feedbackData.dSATOptions = [
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption1,
                uiString: loc(SmartPillFeedbackMeetingOption1),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption2,
                uiString: loc(SmartPillFeedbackMeetingOption2),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption3,
                uiString: loc(SmartPillFeedbackMeetingOption3),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption4,
                uiString: loc(SmartPillFeedbackMeetingOption4),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackMeetingOption5,
                uiString: loc(SmartPillFeedbackMeetingOption5),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption7,
                uiString: loc(SmartPillFeedbackGenericOption7),
            },
        ];
    } else if (hasSmartDocPill) {
        // Case 5: Just a smart doc pill by itself
        feedbackData.subtitle = loc(SmartPillFeedbackFileSuggestionsSubtitle);
        feedbackData.dSATOptions = [
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption1,
                uiString: loc(SmartPillFeedbackFileOption1),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption2,
                uiString: loc(SmartPillFeedbackFileOption2),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption3,
                uiString: loc(SmartPillFeedbackFileOption3),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption4,
                uiString: loc(SmartPillFeedbackFileOption4),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackFileOption5,
                uiString: loc(SmartPillFeedbackFileOption5),
            },
            {
                logString: SMART_PILL_LOGSTRING.SmartPillFeedbackGenericOption7,
                uiString: loc(SmartPillFeedbackGenericOption7),
            },
        ];
    } else {
        // Default subtitle, DSAT options to be decided later.
        feedbackData.subtitle = loc(SmartPillFeedbackGenericSubtitle);
    }

    return feedbackData;
}
